import { useContext, useEffect } from 'react';
import { cn } from '@/helpers/utils/functionality-utils';
import {
  Authenticator,
  ThemeProvider,
  type Theme,
  useAuthenticator,
  defaultDarkModeOverride,
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { LayoutContext } from '@/layout/context/context';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { commonActions } from '@/store/slices/commonSlice';

const LoginPage = () => {
  const { layoutConfig, theme } = useContext(LayoutContext);
  const dispatch = useAppDispatch();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const nav = useNavigate();
  const redirectUrl = useAppSelector((state) => state.common.redirectUrl);

  const containerClassName = cn(
    'flex min-h-screen min-w-[100vw] items-center justify-center overflow-hidden bg-surface-50',
    { 'p-input-filled': layoutConfig.inputStyle === 'filled' },
  );

  const customTheme: Theme = {
    name: 'custom-theme',
    overrides: [defaultDarkModeOverride],
    tokens: {
      components: {
        authenticator: {
          router: {
            borderWidth: { value: '0px' },
            boxShadow: { value: 'none' },
          },
        },
      },
    },
  };

  useEffect(() => {
    if (authStatus === 'authenticated') {
      if (redirectUrl) {
        nav(redirectUrl, {
          viewTransition: true,
        });
        dispatch(commonActions.setRedirectUrl(''));
      } else {
        nav('/main/dashboard', {
          viewTransition: true,
        });
      }
    }
  }, [authStatus, nav]);
  if (authStatus === 'authenticated' || authStatus === 'configuring') {
    // residue ui remains after login, this code-block ensures to hide it.
    return null;
  }

  return (
    <div className={containerClassName}>
      <meta name="robots" content="noindex, nofollow, noarchive" />
      <div className="flex flex-col items-center justify-center">
        <img
          src={`/assets/images/logo/logo-${
            theme === 'light' ? 'dark' : 'light'
          }-512.png`}
          alt="Sakai logo"
          style={{ height: '200px', width: '200px' }}
          className="mb-8 shrink-0"
        />
        <div
          style={{
            borderRadius: '56px',
            padding: '0.3rem',
            background:
              'linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)',
          }}
        >
          <div
            className="w-full px-8 py-8"
            style={{
              borderRadius: '53px',
              background: 'var(--surface-section)',
            }}
          >
            <div className="mb-8 text-center">
              <span className="text-lg font-bold leading-10 text-surface-600">
                LOGIN
              </span>
            </div>

            <div>
              <ThemeProvider
                colorMode={theme as 'light' | 'dark'}
                theme={customTheme}
              >
                <Authenticator
                  hideSignUp
                  loginMechanisms={['email']}
                ></Authenticator>
              </ThemeProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
